// ---------------------------------------------------------------------------
// Which page each route renders (R12c)
//
// @adornis/router is gone from the app. The router is the app's own
// (router/: the route table, matching, history, leave hooks, links); what a
// page change does to the title, <html lang>, scroll and focus is
// router/page-change.ts's; the page is mounted again per page by
// router/remount.ts; the console frame is shell/frame.ts (R12d), which asks
// before signing out and lets the page's leave hooks run first. What stays
// here is what needs the pages: the render map, and the frame with the
// router and the REST session's sign-out.
// Pages import the router from router/app-router.ts, not from here.
// R13: signing out is the REST session's (auth/session.ts); a sign-out the
// server could not complete, or that did not reach it, is said in a toast.
// ---------------------------------------------------------------------------

import { AssessmentPage } from './assessment/assessment-page.js';
import { appSession } from './auth/app-session.js';
import { AdminDashboard } from './pages/admin-dashboard.js';
import { AdminLogin } from './pages/admin-login.js';
import { CampaignDetail } from './pages/campaign-detail.js';
import { CampaignWizard } from './pages/campaign-wizard.js';
import { Connected } from './pages/connected.js';
import { Dashboard } from './pages/dashboard.js';
import { OptOutPage } from './pages/opt-out.js';
import { Settings } from './pages/settings.js';
import { sdrRouter } from './router/app-router.js';
import { createAppFrame } from './shell/frame.js';
import { appOutlet, type PageMap } from './shell/outlet.js';
import { toast } from './toast.js';

export { sdrRouter };

/** The page per route. A blank campaign id never gets here: the outlet shows "Invalid campaign ID." */
const PAGES: PageMap = {
  dashboard: () => Dashboard(),
  newCampaign: () => CampaignWizard({}),
  editCampaign: ({ campaignId }) => CampaignWizard({ campaignId }),
  campaignDetail: ({ campaignId }) => CampaignDetail({ campaignId: campaignId as string }),
  settings: () => Settings(),
  assessment: () => AssessmentPage({}),
  optOut: () => OptOutPage(),
  adminLogin: () => AdminLogin(),
  admin: () => AdminDashboard(),
  connected: () => Connected(),
};

/** Signs out over REST (R13): a sign-out the server could not complete, or that did not reach it, is said in a toast. */
async function signOut() {
  const result = await appSession.signOut();
  if (!result.ok) toast.error(result.error);
  else if (result.warning) toast.warning(result.warning);
}

/** The frame: the router to move and to run the leave hooks, and how to sign out. startup.ts seeds its theme. */
export const sdrFrame = createAppFrame({ nav: sdrRouter, signOut });

/** The current route's page, in its frame. */
export function sdrOutlet() {
  return appOutlet({ state: sdrRouter.state.value, pages: PAGES, frame: sdrFrame });
}
